import Box from '../Box';
import Text from '../Text';
import { SwitchWrapper, Toggle, SwitchStyleProps } from './Switch.styles';

export interface SwitchProps extends SwitchStyleProps {
  /** Whether the switch is currently on. */
  isOn: boolean;
  onClick?: () => void;
  disabled?: boolean;
  /** Renders an "On"/"Off" label beside the toggle. */
  onOffLabel?: boolean;
  /**
   * Test selector, applied as `data-name` on the toggle. When `onOffLabel` is
   * set, the label gets `${dataName}-text`.
   *
   * Note this is `dataName`, not `data-name` — the component reads named props
   * and does not spread the rest, so a raw `data-name` would be dropped.
   */
  dataName?: string;
}

/**
 * On/off toggle. Controlled: it renders `isOn` and reports clicks, and never
 * holds state of its own.
 */
export default function Switch({
  isOn,
  onClick,
  disabled,
  onOffLabel,
  wrapperHeight,
  wrapperWidth,
  toggleHeight,
  toggleWidth,
  toggleBottom,
  toggleShadow,
  toggleLeft,
  dataName,
}: SwitchProps) {
  return (
    <Box horizontal flow={7} align="center">
      <SwitchWrapper {...{ wrapperHeight, wrapperWidth, disabled }}>
        <Toggle
          role="switch"
          aria-checked={isOn}
          data-name={dataName}
          aria-disabled={disabled || undefined}
          {...{
            isOn,
            onClick,
            toggleHeight,
            toggleWidth,
            toggleBottom,
            toggleShadow,
            toggleLeft,
          }}
        />
      </SwitchWrapper>
      {onOffLabel && (
        <Text
          size="small"
          color={isOn ? 'success' : 'secondaryText'}
          data-name={dataName ? `${dataName}-text` : undefined}
        >
          {isOn ? 'On' : 'Off'}
        </Text>
      )}
    </Box>
  );
}
